"use client";

import authApi from "@/api/auth";
import StoreDropdown from "@/components/StoreDropdown";
import { CONSTANTS_KEYS } from "@/constants/store";
import { User } from "@/interfaces/user.interface";
import Cookies from "js-cookie";
import {
  Brush,
  Tag as Campaign,
  LayoutTemplate,
  LibraryBig,
  PanelLeftClose,
  PanelLeftOpen,
  ShoppingCart,
  Store,
  FileText,
  Settings,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Tag } from "primereact/tag";
import { RefObject, Suspense, useEffect, useState } from "react";
import { useClickOutside } from "primereact/hooks";
import React from "react";
import { APP_VERSION } from "@/constants/version";

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const [loginInformation, setLoginInformation] = useState<User | null>(null);

  const router = useRouter();
  const pathname = usePathname();
  const excludePaths = [
    "/login",
    "/register",
    "/forgot-password",
    "/artworks/",
    "/customization-form/",
    "/customization-forms/",
    "/variants",
  ];
  const navigationLinks = [
    {
      label: "Home",
      path: "/home",
      icon: <Store size={18} strokeWidth={2} />,
    },
    {
      label: "Artworks",
      path: "/artworks",
      icon: <Brush size={18} strokeWidth={2} />,
    },
    {
      label: "Templates",
      path: "/templates",
      icon: <LayoutTemplate size={18} strokeWidth={2} />,
    },

    {
      label: "Libraries",
      path: "/libraries",
      icon: <LibraryBig size={18} strokeWidth={2} />,
    },
    {
      label: "Forms",
      path: "/customization-forms",
      icon: <FileText size={18} strokeWidth={2} />,
    },
    {
      label: "Campaigns",
      path: "/campaigns",
      icon: <Campaign size={18} strokeWidth={2} />,
    },
    {
      label: "Orders",
      icon: <ShoppingCart size={18} strokeWidth={2} />,
      children: [
        { label: "Orders", path: "/orders" },
        {
          label: "Carts",
          path: "/customizations",
        },
      ],
    },
    {
      label: "Advances",
      path: "/advances",
      icon: <Settings size={18} strokeWidth={2} />,
    },
  ];
  useEffect(() => {
    const user = Cookies.get(CONSTANTS_KEYS.USER);
    if (user) {
      setLoginInformation(JSON.parse(user));
    }
  }, [pathname]);
  const isExcludedPath = excludePaths.some((path) => pathname.includes(path));
  if (isExcludedPath) {
    return <>{children}</>;
  }
  const onLogout = () => {
    authApi.logout().catch(() => {});
    Cookies.remove(CONSTANTS_KEYS.AUTH_TOKEN);
    Cookies.remove(CONSTANTS_KEYS.REFRESH_TOKEN);
    Cookies.remove(CONSTANTS_KEYS.USER);
    localStorage.removeItem(CONSTANTS_KEYS.SELECTED_STORE);
    setLoginInformation(null);
    router.push("/login");
  };

  return (
    <div className="flex h-screen w-screen bg-gray-100">
      <aside
        className={`fixed z-10 h-full bg-white shadow-lg transition-all duration-300 ${
          sidebarCollapsed ? "w-12" : "w-50"
        }`}
        // onMouseOver={() => setSidebarCollapsed(false)}
        // onMouseLeave={() => setSidebarCollapsed(true)}
      >
        <div className="flex items-center justify-between gap-1 border-b border-gray-200 py-2">
          <Image
            crossOrigin="anonymous"
            src="/logo.png"
            alt="Logo"
            width={42}
            height={42}
            className="shrink-0 grow-0"
          />
          <h2
            className={`relative overflow-x-hidden text-base font-bold whitespace-nowrap text-gray-800 transition-all ${
              sidebarCollapsed ? "w-0" : "w-full"
            }`}
          >
            BuildYou Nova
            {/* <span className="absolute -top-1 right-2 text-[10px] font-normal text-gray-500">
              {APP_VERSION}
            </span>
             */}
          </h2>
        </div>

        <nav className="mt-4">
          <ul>
            {navigationLinks.map((link) => {
              return (
                <RenderItem
                  item={link}
                  sidebarCollapsed={sidebarCollapsed}
                  key={link.label}
                />
              );
            })}
          </ul>
          <ul className="absolute bottom-0 left-0 mt-4 w-full">
            <li>
              <div
                className="flex w-full cursor-pointer items-center justify-end"
                style={{
                  padding: "12px",
                }}
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              >
                {sidebarCollapsed ? (
                  <PanelLeftOpen
                    className="cursor-pointer text-gray-400"
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  />
                ) : (
                  <PanelLeftClose
                    className="cursor-pointer text-gray-400"
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  />
                )}
              </div>
            </li>
          </ul>
        </nav>
      </aside>
      <main
        className={`box-border flex h-full w-full flex-1 flex-col gap-2 p-4 transition-all duration-300 ${
          sidebarCollapsed ? "pl-17" : "pl-54"
        }`}
      >
        <div className="flex items-center justify-between">
          <Suspense fallback={null}>
            <StoreDropdown />
          </Suspense>
          <div className="flex items-center gap-1">
            <Link href="/guide" style={{ lineHeight: 1 }}>
              <i
                className="pi pi-book"
                style={{ fontSize: "1.25rem", color: "#607d8b" }}
              ></i>
            </Link>
            <Link href="/setting" style={{ lineHeight: 1 }}>
              <i
                className="pi pi-spin pi-cog"
                style={{
                  fontSize: "1.25rem",
                  marginLeft: "8px",
                  color: "#607d8b",
                }}
              ></i>
            </Link>

            {loginInformation ? (
              <UserTagName
                role={loginInformation?.roles?.[0]?.name || ""}
                userName={loginInformation?.userName}
                isSuperAdmin={loginInformation?.isSuperAdmin === true}
                onLogout={onLogout}
              />
            ) : (
              <Tag
                severity="contrast"
                value="Login"
                onClick={() => router.push("/login")}
                className="cursor-pointer text-2xl whitespace-nowrap"
              ></Tag>
            )}
          </div>
        </div>
        <div className="w-full flex-1 overflow-auto rounded-lg bg-white">
          {children}
        </div>
      </main>
    </div>
  );
}
interface SideBarItemProps {
  label: string;
  icon?: React.ReactNode;
  path?: string;
  children?: SideBarItemProps[];
}
const RenderItem = ({
  item,
  sidebarCollapsed,
}: {
  item: SideBarItemProps;
  sidebarCollapsed: boolean;
}) => {
  const pathname = usePathname();
  const isActive = item.path && pathname.includes(item.path);
  const [childCollapsed, setChildCollapsed] = useState<boolean>(true);
  return (
    <li key={item.path} style={{ padding: "0 8px", margin: "2px 0px" }}>
      {item.path ? (
        <Link
          href={item.path}
          className={`flex w-full cursor-pointer items-center gap-2 p-2 ${
            isActive ? "p-button" : "hover:bg-gray-300"
          }`}
          style={{
            borderRadius: 6,
            padding: sidebarCollapsed ? "8px 0px" : "8px 12px",
          }}
        >
          <span></span>
          <span className="shrink-0 grow-0">
            {item.icon}
            {/* <i
              className={`pi ${} text-xl font-bold ${
                isActive ? "text-white" : "text-gray-600"
              }`}
            ></i> */}
          </span>
          <span
            className={`block overflow-x-hidden text-left whitespace-nowrap transition-all ${
              sidebarCollapsed ? "w-0" : "w-full"
            }`}
          >
            {item.label}
          </span>
        </Link>
      ) : (
        <div
          className={`flex w-full cursor-pointer items-center gap-2 p-2 ${
            isActive ? "p-button" : "hover:bg-gray-300"
          }`}
          style={{
            borderRadius: 6,
            padding: sidebarCollapsed ? "8px 0px" : "8px 12px",
          }}
          onClick={() => setChildCollapsed(!childCollapsed)}
        >
          <span></span>
          <span className="shrink-0 grow-0">
            {item.icon}
            {/* <i
              className={`pi ${item.icon} text-xl font-bold ${
                isActive ? "text-white" : "text-gray-600"
              }`}
            ></i> */}
          </span>
          <span
            className={`block overflow-x-hidden text-left whitespace-nowrap transition-all ${
              sidebarCollapsed ? "w-0" : "w-full"
            }`}
          >
            {item.label}
          </span>
          {/* <span className="ml-auto"></span> */}
          {/* <i
            className={`pi pi-chevron-right font-bold text-gray-600`}
            style={{
              display: sidebarCollapsed ? "none" : "block",
              transform: childCollapsed ? "rotate(0deg)" : "rotate(90deg)",
              fontSize: "0.75rem",
              transition: "transform 0.3s ease-in-out",
            }}
          ></i> */}
        </div>
      )}
      {item.children && !sidebarCollapsed && (
        <ul
          className="ml-6 pl-2"
          style={{
            // height: childCollapsed ? "0" : item.children.length * 48 + "px",
            overflow: "hidden",
            transition: "height 0.3s ease-in-out",
          }}
        >
          {item.children.map((child) => {
            const isChildActive = child.path && pathname.includes(child.path);
            return (
              <li key={child.path || child.label} style={{ margin: "2px 0px" }}>
                {child.path ? (
                  <Link
                    href={child.path}
                    className={`flex w-full cursor-pointer items-center gap-2 p-2 ${
                      isChildActive ? "p-button" : "hover:bg-gray-300"
                    }`}
                    style={{
                      borderRadius: 6,
                      padding: sidebarCollapsed ? "4px 0px" : "4px 12px",
                    }}
                  >
                    <span className="block overflow-x-hidden text-left whitespace-nowrap">
                      {child.label}
                    </span>
                  </Link>
                ) : (
                  <div
                    className={`flex w-full cursor-pointer items-center gap-2 p-2 hover:bg-gray-300 ${
                      isChildActive ? "p-button" : ""
                    }`}
                    style={{
                      borderRadius: 0,
                      padding: "12px",
                    }}
                  >
                    <span className="block overflow-x-hidden text-left whitespace-nowrap">
                      {child.label}
                    </span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
};

const UserTagName = ({
  userName,
  role,
  isSuperAdmin,
  onLogout,
}: {
  role: string;
  userName: string;
  isSuperAdmin?: boolean;
  onLogout: () => void;
}) => {
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const actionValueRef = React.useRef<HTMLDivElement>(null);
  useClickOutside(actionValueRef as RefObject<HTMLDivElement>, () => {
    setCollapsed(true);
  });
  return (
    <div className="relative h-max w-max" ref={actionValueRef}>
      <div
        onClick={() => setCollapsed(!collapsed)}
        className="flex cursor-pointer items-center gap-[5px] rounded-lg p-[6px_8px] transition-all duration-200 hover:bg-gray-200 hover:shadow-sm"
      >
        <div className="relative">
          <Image
            crossOrigin="anonymous"
            src="/images/default_avatar.png"
            alt="Avatar"
            width="32"
            height="32"
          />
          <span className="absolute top-0 right-0 h-[10px] w-[10px] rounded-full border-2 border-white bg-green-500" />
        </div>

        <div
          className="flex flex-col justify-center"
          style={{
            lineHeight: "1.1",
            paddingTop: "1px",
            paddingBottom: "1px",
          }}
        >
          <span
            style={{ fontWeight: "600", fontSize: "14px", color: "#607d8b" }}
          >
            {`Hi, ${userName}`}
          </span>
          <p
            style={{
              fontSize: "12px",
              color: isSuperAdmin ? "#b91c1c" : "#777",
              textTransform: "capitalize",
              fontWeight: isSuperAdmin ? 600 : undefined,
            }}
          >
            {isSuperAdmin ? "Super Admin" : role.toLowerCase()}
          </p>
        </div>
      </div>

      <div
        className={`absolute right-0 z-10 w-full min-w-40 rounded-md bg-white shadow-lg transition-all duration-300 ${
          collapsed ? "h-0 overflow-hidden" : "h-max"
        }`}
      >
        <Link
          href="/profile"
          className="flex cursor-pointer items-center gap-2 p-2 px-4 hover:bg-gray-100"
          onClick={() => {
            setCollapsed(!collapsed);
          }}
        >
          <i className="pi pi-user text-gray-600"></i>
          <span className="text-md text-gray-600">Profile</span>
        </Link>

        <div
          className="flex cursor-pointer items-center gap-2 p-2 px-4 hover:bg-gray-100"
          onClick={onLogout}
        >
          <i className={`pi pi-sign-out shrink-0 grow-0 text-xl font-bold`}></i>
          <span className="text-md text-gray-600">Logout</span>
        </div>

        {/* <button
          className={`flex w-max cursor-pointer items-center gap-2 rounded-md hover:bg-red-100`}
          style={{
            padding: "8px",
          }}
        ></button> */}
      </div>
    </div>
  );
};
